import React, { useCallback, useMemo } from 'react';
import { Box, Button, CircularProgress, Collapse, Grow, Paper, Stack, Typography } from '@mui/material';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { ContentControlButton } from './ContentControl/ContentControlButton';
import { TaskCollection } from '@ekkogmbh/apisdk';
import { Check, Warning } from '@mui/icons-material';
import { ContentPanel, PanelProvider, StepDefinition } from './ContentControl/ContentPanel';
import { TaskCollectionProgressBar } from 'src/TaskManagement/Components/TaskCollectionProgressBar';
import { StyledFormHeader } from './Forms/StyledFormHeader';
import { useContentControl } from '../Stores/ContentControlProvider';
import useTaskCollections from '../Providers/TaskCollectionProvider';

const TasksPanel = ({ taskCollections }: { taskCollections: TaskCollection[] }): React.JSX.Element => {
  const { setPanelOpen } = useContentControl();
  const contentCallback = useCallback((): React.JSX.Element => {
    if (taskCollections.length === 0) {
      return (
        <Stack margin={1} alignItems={'center'} justifyContent={'space-between'}>
          <Typography textAlign={'center'}>{'your tasks have been finished'}</Typography>
          <Button variant={'outlined'} onClick={() => setPanelOpen(false)}>
            close
          </Button>
        </Stack>
      );
    }

    const progressBars = taskCollections.map((tc, i) => <TaskCollectionProgressBar key={i} taskCollection={tc} />);

    return (
      <Stack direction={'row'} spacing={1} alignItems={'stretch'} justifyContent={'flex-start'}>
        <StyledFormHeader label={'Task Collections'} />
        <Stack direction={'row'} spacing={3} alignItems={'flex-end'} justifyContent={'flex-start'}>
          <Typography variant={'overline'}>{'ID'}</Typography>
          <Typography variant={'overline'}>{'Progress'}</Typography>
        </Stack>
        {progressBars}
        <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'center'}>
          <Typography variant={'subtitle1'}>{'reloading the page will stop all task progress tracking'}</Typography>
          <Warning color={'secondary'} />
        </Stack>
      </Stack>
    );
  }, [taskCollections]);

  const step: StepDefinition = {
    title: 'Task-Collections',
    contentCallback,
  };

  return <ContentPanel hidePanelButtons steps={[step]} onCancel={() => true} />;
};

const TasksProgress = ({ progress }: { progress: number }): React.JSX.Element => (
  <>
    <Box sx={{ position: 'absolute', alignItems: 'center', justifyContent: 'center' }}>
      <Typography color={'secondary'}>{progress}</Typography>
    </Box>
    <CircularProgress
      size={50}
      variant={progress > 0 ? 'determinate' : 'indeterminate'}
      color={'secondary'}
      value={progress}
    />
  </>
);

const PanelContainer = ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
  <Paper
    elevation={0}
    sx={{
      width: '100%',
      overflow: 'hidden',
      boxShadow: 5,
      m: 0,
      marginTop: -3,
    }}
  >
    <Collapse in={children !== undefined}>
      <PanelProvider>{children}</PanelProvider>
    </Collapse>
  </Paper>
);

interface Props {
  transparent?: boolean;
  panel?: React.JSX.Element;
  dialog?: React.JSX.Element;
  buttons?: React.JSX.Element[];
  children?: React.ReactNode;
}

export const ContentControl = ({ panel, dialog, buttons, children, transparent }: Props): React.JSX.Element => {
  const { taskCollections } = useTaskCollections();
  const { isPanelOpen, togglePanel } = useContentControl();

  const tasksButton = useMemo(() => {
    const hasTasks = taskCollections.length > 0;

    const totalProgress = hasTasks
      ? Math.floor(
          taskCollections.reduce((carry: number, tc: TaskCollection) => carry + (tc.progress ?? 0), 0) /
            taskCollections.length,
        )
      : 100;

    return (
      <Grow in={hasTasks} appear timeout={1000} unmountOnExit>
        <Box component={'div'}>
          <ContentControlButton
            tooltip={'Task Progress'}
            onClick={togglePanel}
            content={totalProgress === 100 ? <Check /> : <TasksProgress progress={totalProgress} />}
          />
        </Box>
      </Grow>
    );
  }, [taskCollections, togglePanel]);

  const buttonStack = useMemo(
    () => (
      <Stack direction={'row'} spacing={2} width={'100%'} justifyContent={'flex-end'}>
        {tasksButton}
        <TransitionGroup
          component={Stack}
          direction={'row'}
          spacing={2}
          sx={{
            '.slide-button-enter': {
              opacity: 0,
              transform: 'translateX(100%)',
            },
            '.slide-button-enter-active': {
              opacity: 1,
              transform: 'translateX(0)',
              transition: 'opacity 300ms ease-out, transform 300ms ease-out',
            },
            '.slide-button-exit': {
              opacity: 1,
              transform: 'translateX(0)',
            },
            '.slide-button-exit-active': {
              opacity: 0,
              transform: 'translateX(100%)',
              transition: 'opacity 300ms ease-out, transform 300ms ease-out',
            },
          }}
        >
          {(buttons ?? []).map((btn) => (
            <CSSTransition key={btn.key} timeout={300} classNames="slide-button">
              <Box component={'div'}>{btn}</Box>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </Stack>
    ),
    [buttons, tasksButton],
  );

  const shownPanel = useMemo(
    () => (isPanelOpen ? (panel ?? <TasksPanel taskCollections={taskCollections} />) : undefined),
    [panel, isPanelOpen, taskCollections],
  );

  return (
    <Box component={'div'}>
      {dialog}
      <Box sx={{ position: 'relative', zIndex: 33, pr: 4 }}>{buttonStack}</Box>
      <Stack direction={'column'} justifyContent={'space-between'}>
        <PanelContainer>{shownPanel}</PanelContainer>
        {isPanelOpen && <Box height={21} />}
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            boxShadow: 5,
            ...(transparent && {
              backgroundColor: 'transparent',
              boxShadow: 0,
            }),
          }}
        >
          {children}
        </Paper>
      </Stack>
    </Box>
  );
};
